// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Client Detail page — Meetings tab.
 *
 * Lists every calendar event linked to this client profile in a
 * configurable window (default 90d back → 90d forward). Auto-links
 * are marked; manual unlink is offered for MANUAL links only.
 */

import { useCallback, useEffect, useState } from 'react';
import { AppIcon, Section, Skeleton } from '@loa/ui-components';
import { integrationsApi, type CalendarEventDTO, type LinkMethod } from '../../api/integrationsApi';

type Props = Readonly<{
  clientId: string;
}>;

function formatDate(iso: string | null): string {
  if (!iso) return 'TBC';
  return new Date(iso).toLocaleString('en-IE', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function linkBadgeText(method: LinkMethod): string {
  switch (method) {
    case 'AUTO_EMAIL_MATCH': return 'Auto-linked';
    case 'MANUAL':           return 'Manual';
    case 'PLATFORM_CREATED': return 'Created here';
    default:                 return method;
  }
}

export function ClientMeetingsTab({ clientId }: Props) {
  const [events, setEvents] = useState<CalendarEventDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await integrationsApi.getMeetingsForClient(clientId);
      setEvents(list);
    } catch (e) {
      setError('Could not load meetings — please try again later.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleUnlink = async (eventId: string) => {
    try {
      setUnlinking(eventId);
      await integrationsApi.unlinkClient(eventId, clientId);
      // Refetch to reflect the deleted link.
      await load();
    } catch (e) {
      setError('Could not unlink — auto-links can only be removed by the linker.');
    } finally {
      setUnlinking(null);
    }
  };

  if (loading) {
    return (
      <Section title="Meetings">
        <Skeleton lines={4} />
      </Section>
    );
  }

  if (error) {
    return (
      <Section title="Meetings">
        <div className="client-meetings-tab__error" role="alert">{error}</div>
      </Section>
    );
  }

  if (events.length === 0) {
    return (
      <Section title="Meetings">
        <div className="client-meetings-tab__empty">
          <AppIcon name="calendar" />
          <p>No meetings linked to this client yet.</p>
          <p className="client-meetings-tab__hint">
            Meetings auto-link when the client&apos;s email is on the attendee list.
          </p>
        </div>
      </Section>
    );
  }

  return (
    <Section title="Meetings">
      <ul className="client-meetings-tab__list">
        {events.map((event) => {
          const ownLink = event.linkedClients.find((l) => l.clientProfileId === clientId);
          const method = ownLink?.linkMethod;
          const canUnlink = method === 'MANUAL';
          return (
            <li
              key={event.eventId}
              className={event.cancelled
                ? 'client-meetings-tab__row client-meetings-tab__row--cancelled'
                : 'client-meetings-tab__row'}
            >
              <div className="client-meetings-tab__when">{formatDate(event.startsAt)}</div>
              <div className="client-meetings-tab__body">
                <div className="client-meetings-tab__subject">
                  {event.subject ?? '(No subject)'}
                </div>
                {event.location && (
                  <div className="client-meetings-tab__location">{event.location}</div>
                )}
                {method && (
                  <span className={`client-meetings-tab__badge client-meetings-tab__badge--${method.toLowerCase()}`}>
                    {linkBadgeText(method)}
                  </span>
                )}
              </div>
              <div className="client-meetings-tab__actions">
                {canUnlink && (
                  <button
                    type="button"
                    onClick={() => handleUnlink(event.eventId)}
                    disabled={unlinking === event.eventId}
                  >
                    {unlinking === event.eventId ? 'Removing…' : 'Unlink'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
